import React from 'react';
import { bool, object, shape, number } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { propTypes } from '../../util/types';
import { withViewport } from '../../util/contextHelpers';

import config from '../../config';
import {
  Page,
  SectionMusteringSteps,
  SectionMusteringHero,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  IconSuccess,
} from '../../components';
import { TopbarContainer } from '..';

import facebookImage from '../../assets/lth-og.jpg';
import twitterImage from '../../assets/lth-twitter.jpg';
import css from './MusteringPage.module.css';
import moment from 'moment-timezone';

export const MusteringPageComponent = props => {
  const {
    history,
    intl,
    location,
    scrollingDisabled,
    currentUserListing = null,
    currentUserListingFetched = false,
  } = props;

  const isDoubleMusterMonth =
    process.env.NODE_ENV === 'test'
      ? false
      : moment.tz('Australia/Melbourne').month() === 2 &&
        moment.tz('Australia/Melbourne').year() === 2026;

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const siteTitle = config.siteTitle;
  const schemaTitle = isDoubleMusterMonth
    ? intl.formatMessage({ id: 'MusteringPage.schemaTitleDoubleMuster' }, { siteTitle })
    : intl.formatMessage({ id: 'MusteringPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage(
    { id: 'MusteringPage.schemaDescription' },
    { siteTitle }
  );
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 1024, height: 512 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.heroContainer}>
            <SectionMusteringHero
              className={css.hero}
              history={history}
              location={location}
              isDoubleMusterMonth={isDoubleMusterMonth}
            />
          </div>
          <div className={css.heroMusterBlurb}>
            {isDoubleMusterMonth ? (
              <div className={css.sectionContent}>
                <h2>
                  This March, we're doubling the reward for the Great Australian Trailer Muster.
                </h2>
                <p>
                  Refer a mate who owns a trailer and you'll earn $100 once their trailer gets its
                  first hire.
                </p>
                <p>It's our biggest referral offer yet – so saddle up, partner!</p>
              </div>
            ) : (
              <div className={css.sectionContent}>
                <h2>Saddle up partners; it's time for the great Australian trailer muster.</h2>
                <p>
                  Simply refer anyone who owns a trailer. After they have listed their trailer and
                  rented it out once we'll send you $50 to say thanks!
                </p>
              </div>
            )}
          </div>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionMusteringSteps isDoubleMusterMonth={isDoubleMusterMonth} />
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2>Why Join the Trailer Muster?</h2>
                <ul>
                  <li>
                    <IconSuccess
                      fillColor={'var(--marketplaceColor)'}
                      className={css.successIcon}
                    />{' '}
                    Help a mate earn money from their trailer
                  </li>
                  <li>
                    <IconSuccess
                      fillColor={'var(--marketplaceColor)'}
                      className={css.successIcon}
                    />{' '}
                    Grow the trailer-sharing community
                  </li>
                  <li>
                    <IconSuccess
                      fillColor={'var(--marketplaceColor)'}
                      className={css.successIcon}
                    />{' '}
                    Earn yourself {isDoubleMusterMonth ? '$100' : '$50'} cash
                  </li>
                </ul>
              </div>
            </li>
            {isDoubleMusterMonth ? (
              <li className={css.section}>
                <div className={css.sectionContent}>
                  <h2>📣 Spread the Word</h2>
                  <p>
                    Share the good word via text, socials, or good old-fashioned banter. Every mate
                    with a trailer is a chance to earn!
                  </p>
                </div>
              </li>
            ) : null}
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2>Terms and Conditions</h2>
                <ol className={css.tAndCsList}>
                  <li>
                    Trailer owners who already have a trailer(s) on www.localtrailerhire.com.au can
                    not be referred as owners although they can refer others.
                  </li>
                  <li>A trailer owner can only be referred once.</li>
                  <li>The referral bonus is payable once the first hire has been completed.</li>
                  <li>
                    The referral bonus will be paid by direct bank transfer and recipients will need
                    to provide details accordingly.
                  </li>
                  <li>The promotion is only open to Australian residents.</li>
                  <li>
                    The overall referral promotion ends 31 January 2027. Referred trailers must be
                    listed by this date to be eligible, but the first hire may occur after this
                    date.
                  </li>
                  {isDoubleMusterMonth ? (
                    <>
                      <hr />
                      <h3>March Double Bonus Terms</h3>
                      <li>
                        During the month of March 2026, the referral bonus is doubled to $100.
                      </li>
                      <li>
                        To qualify for the $100 bonus, the referred trailer must be listed between 1
                        March and 31 March 2026.
                      </li>
                      <li>
                        The first hire can take place after March, and the $100 bonus will still be
                        valid as long as the trailer was listed during March 2026.
                      </li>
                      <hr />
                    </>
                  ) : null}
                  <li>
                    We retain the absolute right to refuse to pay or cancel a referral bonus payment
                    without notice to you. The reasons for us to refuse or cancel a payment may
                    include, but are not limited to, if:
                    <ul>
                      <li>
                        we believe that your access to localtrailerhire.com.au is or may be
                        fraudulent
                      </li>
                      <li>
                        or we believe that you have threatened the security of
                        localtrailerhire.com.au
                      </li>
                      <li>or we believe you have breached any of our terms</li>
                      <li>or we believe you are acting in bad faith</li>
                      <li>
                        or you reside at the same address as a trailer owner already on Local
                        Trailer Hire
                      </li>
                      <li>or we have locked or closed your account.</li>
                    </ul>
                  </li>
                  <li>
                    We also retain the absolute right to stop the offer at any time and to change
                    the conditions or incentive of the offer.
                  </li>
                </ol>
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

MusteringPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { currentUserListing, currentUserListingFetched } = state.user;
  const { isAuthenticated } = state.Auth;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUserListing,
    currentUserListingFetched,
    isAuthenticated,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const MusteringPage = compose(
  withViewport,
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(MusteringPageComponent);

export default MusteringPage;
